import { REAUTH_ATTEMPT_STORAGE_KEY } from "./core.constants";

export default function isTokenValid(token) {
  return token && token.id_token
}

/**
 * Read the timestamp (epoch ms) of the last automatic silent re-auth attempt from sessionStorage,
 * or 0 when there's none / storage is unavailable. Shared by the reactive 401 interceptor and the
 * proactive pre-expiry hook so both honor the same anti-loop window.
 */
export function readLastReauthAttemptMs() {
  try {
    return Number(sessionStorage.getItem(REAUTH_ATTEMPT_STORAGE_KEY)) || 0;
  } catch {
    return 0;
  }
}

export function markReauthAttempt(nowMs) {
  try {
    sessionStorage.setItem(REAUTH_ATTEMPT_STORAGE_KEY, String(nowMs));
  } catch { /* sessionStorage may be unavailable */ }
}

export function clearReauthAttempt() {
  try {
    sessionStorage.removeItem(REAUTH_ATTEMPT_STORAGE_KEY);
  } catch { /* sessionStorage may be unavailable */ }
}

export const generateLoginUrl = () => {
  const { REACT_APP_OAUTH_BASE_URL, REACT_APP_OAUTH_CLIENT_ID, REACT_APP_OAUTH_NONCE, REACT_APP_OAUTH_ACR_VALUES, REACT_APP_OAUTH_PROMPT, REACT_APP_OAUTH_REDIRECT_URI, REACT_APP_OAUTH_SCOPE } = process.env
  const urlReturn = encodeURIComponent(window.location.pathname.padEnd(23, '='));

  const authUrl = `https://${REACT_APP_OAUTH_BASE_URL}/openid_connect/authorize?response_type=code&client_id=${REACT_APP_OAUTH_CLIENT_ID}&prompt=${REACT_APP_OAUTH_PROMPT}&redirect_uri=${REACT_APP_OAUTH_REDIRECT_URI}&scope=${REACT_APP_OAUTH_SCOPE}&state=${urlReturn}&nonce=${REACT_APP_OAUTH_NONCE}&acr_values=${REACT_APP_OAUTH_ACR_VALUES}`;

  return authUrl;
}

// Login.gov's RP-Initiated Logout endpoint requires a `state` value at least 22
// characters long if one is provided. It's only echoed back to
// post_logout_redirect_uri on success, so its content doesn't matter — pad it out
// the same way generateLoginUrl pads its own state (return-path) value above.
const LOGOUT_STATE_MIN_LENGTH = 23;

export const generateLogoutUrl = () => {
  const { REACT_APP_OAUTH_BASE_URL, REACT_APP_OAUTH_CLIENT_ID, REACT_APP_OAUTH_POST_LOGOUT_REDIRECT_URI } = process.env;
  const state = encodeURIComponent('logout'.padEnd(LOGOUT_STATE_MIN_LENGTH, '='));

  const logoutUrl = `https://${REACT_APP_OAUTH_BASE_URL}/openid_connect/logout?client_id=${REACT_APP_OAUTH_CLIENT_ID}&post_logout_redirect_uri=${REACT_APP_OAUTH_POST_LOGOUT_REDIRECT_URI}&state=${state}`;

  return logoutUrl;
}

/**
 * How long after an automatic silent re-authentication we treat a fresh 401 as a failed re-auth
 * (rather than a normal expired-token event). Prevents an infinite redirect loop when a token is
 * rejected for a reason re-auth can't fix (e.g. a revoked/deactivated account).
 */
export const REAUTH_RETRY_WINDOW_MS = 30 * 1000;

/**
 * Decide whether an active-session 401 should trigger a silent re-authentication.
 *
 * Returns true for a normal expired-token 401 (no recent re-auth attempt). Returns false when we
 * already bounced the user through Login.gov within {@link REAUTH_RETRY_WINDOW_MS} and still got a
 * 401 — that means re-auth didn't resolve it, so the caller should stop looping and fall back.
 *
 * @param {number} nowMs current time (Date.now()).
 * @param {number} lastReauthMs timestamp of the last auto re-auth attempt (0/falsy if none).
 * @param {number} [windowMs] retry window; defaults to {@link REAUTH_RETRY_WINDOW_MS}.
 * @returns {boolean}
 */
export function shouldReauthenticateOnExpiry(nowMs, lastReauthMs, windowMs = REAUTH_RETRY_WINDOW_MS) {
  if (!lastReauthMs) {
    return true;
  }
  return nowMs - lastReauthMs >= windowMs;
}

/**
 * Decode the (unverified) claims payload of a JWT. Signature is NOT checked — this is only for
 * reading non-sensitive client-side hints such as the `exp` expiry. Returns null on any malformed
 * input.
 *
 * @param {string} jwt a compact JWS string (`header.payload.signature`).
 * @returns {object|null}
 */
export function decodeJwtPayload(jwt) {
  if (!jwt || typeof jwt !== "string") {
    return null;
  }

  const parts = jwt.split(".");
  if (parts.length < 2) {
    return null;
  }

  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padLength = (4 - (base64.length % 4)) % 4;
    const padded = base64.padEnd(base64.length + padLength, "=");
    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Resolve the absolute expiry time (epoch ms) of the current session token from the `exp` claim of
 * its `id_token`. Returns null when there's no token or no usable `exp`.
 *
 * @param {{ id_token?: string }|null} token the stored JWT payload.
 * @returns {number|null}
 */
export function getTokenExpiryMs(token) {
  const payload = decodeJwtPayload(token?.id_token);
  if (!payload || typeof payload.exp !== "number") {
    return null;
  }
  return payload.exp * 1000;
}
